// src/utils/meetUtils.js

/**
 * Reemplaza las comillas (") que pudieran generarse en el CSV.
 *
 * @param {string} csvString - La cadena de texto CSV generada por papaparse
 * @returns {string} CSV sin comillas.
 */
export function removeCsvQuotes(csvString) {
  return csvString.replace(/"/g, '');
}

/**
 * Añade el BOM (Byte Order Mark) a un CSV para que Excel reconozca UTF-8.
 * Esto ayuda a que se muestren correctamente los caracteres acentuados y la ñ.
 *
 * @param {string} csvString - El contenido CSV al que se le agregará el BOM
 * @returns {string} El contenido CSV con BOM al inicio
 */
export function addBOM(csvString) {
  // \ufeff es el BOM para UTF-8
  return '\ufeff' + csvString;
}

export function forceExcelText(value, forceText) {
  if (!value) return '';
  if (!forceText) return String(value);

  const stringValue = String(value);
  // Añadir comilla simple al inicio y al final
  return `'${stringValue.replace(/'/g, "'")}`;
}


/**
 * Obtiene el teamCode y teamName por defecto, usando "UNA" y "Unattached"
 * si no existe un objeto equipo, o si falta alguna propiedad.
 *
 * @param {object|string} equipo - El equipo de la inscripción (puede ser objeto o string)
 * @returns {{teamCode: string, teamName: string}}
 */
export function getDefaultTeam(equipo) {
  let teamCode = 'UNA';
  let teamName = 'Unattached';

  if (equipo && typeof equipo === 'object') {
    teamCode = equipo.teamCode || 'UNA';
    teamName = equipo.name || 'Unattached';
  } else if (typeof equipo === 'string') {
    teamName = equipo.trim() || 'Unattached';
  }

  return { teamCode, teamName };
}

/**
 * Formatea el apellido tomando el primer apellido completo y la inicial del segundo.
 * Ejemplo: "Alvarez Salinas" -> "Alvarez S"
 *
 * @param {string} apellido - El apellido completo
 * @returns {string} Apellido formateado
 */
export function formatLastName(apellido = '') {
  const tokens = apellido.trim().split(/\s+/);
  if (tokens.length >= 2) {
    return `${tokens[0]} ${tokens[1].charAt(0)}`;
  }
  return apellido;
}

/**
 * Formatea la fecha de nacimiento a MM/DD/YYYY.
 *
 * @param {object} insc - Objeto con diaNacimiento, mesNacimiento y anioNacimiento
 * @returns {string} Fecha en formato MM/DD/YYYY
 */
export function formatBirthDate(insc) {
  const { anioNacimiento, mesNacimiento, diaNacimiento } = insc;
  if (!anioNacimiento || !mesNacimiento || !diaNacimiento) return '';
  const month = String(mesNacimiento).padStart(2, '0');
  const day = String(diaNacimiento).padStart(2, '0');
  return `${month}/${day}/${anioNacimiento}`;
}

/**
 * Determina el género según la rama: "F" si contiene "fem", "M" si contiene "var".
 *
 * @param {string} rama
 * @returns {string} "F", "M" o cadena vacía si no coincide
 */
export function getGender(rama = '') {
  const lower = rama.toLowerCase();
  if (lower.includes('fem')) return 'F';
  if (lower.includes('var')) return 'M';
  if (lower.includes('mixto')) return 'X';
  return '';
}

/**
 * Normaliza la dirección para que no se parta en líneas distintas o se separe
 * por saltos de línea o tabulaciones.
 *
 * @param {string} address - La dirección original
 * @returns {string} Dirección en una sola línea
 */
export function normalizeAddress(address = '') {
  return address
    .replace(/,/g, ' ')       // Reemplaza comas por espacio
    .replace(/[\r\n\t]+/g, ' ') // Reemplaza saltos de línea y tabulaciones por espacio
    .trim();
}

/**
 * Formatea la marca de siembra para la exportación COMPLETA.
 * Maneja tipos: velocidad, campo, fondo, mediofondo, combinados.
 *
 * @param {object} pr - Objeto de la prueba, con tipoMarca y las propiedades de marca
 * @returns {string} Marca formateada
 */
export function formatMarkForComplete(pr) {
  if (!pr) return '';
  const tipo = pr.tipoMarca ? pr.tipoMarca.toLowerCase() : '';

  if (tipo === 'velocidad') {
    // Velocidad: ss.tt
    if (pr.marcaSegundos !== undefined) {
      const seg = String(pr.marcaSegundos).padStart(2, '0');
      const cent = pr.marcaCentesimas !== undefined
        ? String(pr.marcaCentesimas).padStart(2, '0')
        : '00';
      return `${seg}.${cent}`;
    }
    return '';
  } else if (tipo === 'campo') {
    // Campo: metros.centimetros
    if (pr.marcaMetros !== undefined && pr.marcaCentimetros !== undefined) {
      return `${pr.marcaMetros}.${pr.marcaCentimetros}`;
    }
    return '';
  } else if (tipo === 'fondo') {
    // Fondo: hh:mm:ss.tt
    const hrs = pr.marcaHoras !== undefined
      ? String(pr.marcaHoras).padStart(2, '0')
      : '00';
    const mins = pr.marcaMinutos !== undefined
      ? String(pr.marcaMinutos).padStart(2, '0')
      : '00';
    const secs = pr.marcaSegundos !== undefined
      ? String(pr.marcaSegundos).padStart(2, '0')
      : '00';
    const cent = pr.marcaCentesimas !== undefined
      ? String(pr.marcaCentesimas).padStart(2, '0')
      : '00';
    return `${hrs}:${mins}:${secs}.${cent}`;
  } else if (tipo === 'mediofondo') {
    // Mediofondo: mm:ss.tt
    const mins = pr.marcaMinutos !== undefined
      ? String(pr.marcaMinutos).padStart(2, '0')
      : '00';
    const secs = pr.marcaSegundos !== undefined
      ? String(pr.marcaSegundos).padStart(2, '0')
      : '00';
    const cent = pr.marcaCentesimas !== undefined
      ? String(pr.marcaCentesimas).padStart(2, '0')
      : '00';
    return `${mins}:${secs}.${cent}`;
  } else if (tipo === 'combinados') {
    // Combinados: 4 dígitos
    return pr.marcaPuntos !== undefined
      ? String(pr.marcaPuntos).padStart(4, '0')
      : '';
  }

  // Fallback
  const parts = [];
  if (pr.marcaHoras) parts.push(pr.marcaHoras);
  if (pr.marcaMinutos) parts.push(String(pr.marcaMinutos).padStart(2, '0'));
  if (pr.marcaSegundos) parts.push(String(pr.marcaSegundos).padStart(2, '0'));
  let res = parts.join(':');
  if (pr.marcaCentesimas) {
    res += `.${String(pr.marcaCentesimas).padEnd(2, '0')}`;
  }
  return res;
}

/**
 * Formatea la marca de siembra para Meet Manager (Reporte D).
 * Maneja los mismos tipos, pero con la salida que requiere Hy-Tek.
 *
 * @param {object} event - Objeto de la prueba, con tipoMarca y las propiedades de marca
 * @returns {string} Marca formateada para Hy-Tek
 */
export function formatMeetEntryMark(event) {
  if (!event) return '';
  const tipo = event.tipoMarca ? event.tipoMarca.toLowerCase() : '';

  if (tipo === 'velocidad') {
    // ss.tt
    if (event.marcaSegundos !== undefined) {
      const seg = String(event.marcaSegundos).padStart(2, '0');
      const cent = event.marcaCentesimas !== undefined
        ? String(event.marcaCentesimas).padStart(2, '0')
        : '00';
      return `${seg}.${cent}`;
    }
    return '';
  } else if (tipo === 'campo') {
    // metros.centimetros
    if (event.marcaMetros !== undefined && event.marcaCentimetros !== undefined) {
      return `${event.marcaMetros}.${event.marcaCentimetros}`;
    }
    return '';
  } else if (tipo === 'fondo') {
    // hh:mm.ss.tt
    const hrs = event.marcaHoras !== undefined
      ? String(event.marcaHoras).padStart(2, '0')
      : '00';
    const mins = event.marcaMinutos !== undefined
      ? String(event.marcaMinutos).padStart(2, '0')
      : '00';
    const secs = event.marcaSegundos !== undefined
      ? String(event.marcaSegundos).padStart(2, '0')
      : '00';
    const cent = event.marcaCentesimas !== undefined
      ? String(event.marcaCentesimas).padStart(2, '0')
      : '00';
    return `${hrs}:${mins}.${secs}.${cent}`;
  } else if (tipo === 'mediofondo') {
    // mm:ss.tt
    const mins = event.marcaMinutos !== undefined
      ? String(event.marcaMinutos).padStart(2, '0')
      : '00';
    const secs = event.marcaSegundos !== undefined
      ? String(event.marcaSegundos).padStart(2, '0')
      : '00';
    const cent = event.marcaCentesimas !== undefined
      ? String(event.marcaCentesimas).padStart(2, '0')
      : '00';
    return `${mins}:${secs}.${cent}`;
  } else if (tipo === 'combinados') {
    // 4 dígitos
    return event.marcaPuntos !== undefined
      ? String(event.marcaPuntos).padStart(4, '0')
      : '';
  } else {
    // Fallback
    const parts = [];
    if (event.marcaHoras) parts.push(event.marcaHoras);
    if (event.marcaMinutos) parts.push(String(event.marcaMinutos).padStart(2, '0'));
    if (event.marcaSegundos) parts.push(String(event.marcaSegundos).padStart(2, '0'));
    let res = parts.join(':');
    if (event.marcaCentesimas) {
      res += `.${String(event.marcaCentesimas).padEnd(2, '0')}`;
    }
    return res;
  }
}
